import { Injectable } from "@angular/core";
import { AssessmentsTemplate } from "src/app/api/models/assessments/assessment-template.interface";
import { AssessmentQuestionTypeEnum, AssessmentTemplateEnum } from "src/app/api/models/assessments/assessment.enum";
import { AssessmentPage, AssessmentQuestion, AssessmentSection, AssessmentsMineOSFormControl, AssessmentsMineOSTemplate, AssessmentsMineOSTemplateResponse } from "../models/assessments.interface";
import { AssessmentCustomFieldText, AssessmentCustomLabel, AssessmentDropdownOption, AssessmentPageRequest, AssessmentPageResponse, AssessmentQuestionRequest, AssessmentResourceId, AssessmentResourceValue, AssessmentSectionRequest, AssessmentSectionResponse } from "src/app/api/models/assessments/assessment.interface";
import { FieldInputType } from "src/app/api/models/company-settings/custom-fields.enum";
import { CustomFieldInputTypeEnum } from "src/app/api/models/company-settings/custom-field-v2.enum";
import { CustomFieldsV2Service } from "src/app/company-settings/custom-fields-v2/custom-fields-v2.service";
import { CamelCaseWithSpacesPipe } from "src/app/shared/pipes/camel-case-with-spaces.pipe";
import { CustomEntityChipBackgroundColorPipe } from "../pipes/custom-entity-chip-color.pipe";
import { DropdownOption } from "src/app/shared/mine-dropdown/mine-dropdown.interface";
import { RandomUUIDPipe } from "src/app/shared/pipes/random-uuid.pipe";
import { LoggerService } from "src/app/logger/logger.service";
import { UnsupportedQuestionTypeError } from "../models/unsupported-question-type.error";
import { AssessmentsLabelsService } from "./assessments-labels.service";
import {
    createBaseAssessmentRequest as coreCreateBaseAssessmentRequest,
    createLayoutObject as coreCreateLayoutObject,
    deepJsonParse as coreDeepJsonParse,
    getPageOrdinality as coreGetPageOrdinality,
    getQuestionTypeByValue as coreGetQuestionTypeByValue,
    getSectionOrdinality as coreGetSectionOrdinality,
    normalizeOptionLayout as coreNormalizeOptionLayout,
    normalizeString as coreNormalizeString,
    AssessmentQuestionRequest as CoreQuestionRequest,
    AssessmentSectionRequest as CoreSectionRequest,
} from "@mineos/assessment-core";

/**
 * Values of `AssessmentQuestionTypeEnum` that exist client-side only. All three are flagged
 * deprecated in `assessment.enum.ts` and have no counterpart on the backend enum.
 */
const CLIENT_ONLY_QUESTION_TYPES: readonly AssessmentQuestionTypeEnum[] = [
  AssessmentQuestionTypeEnum.Group,
  AssessmentQuestionTypeEnum.NewSection,
  AssessmentQuestionTypeEnum.CustomComponent,
];

/**
 * Client-side mirror of the backend `AssessmentQuestionType` enum: the only question types the
 * assessments API can bind. A question sent with anything else - including a missing type - is
 * rejected server-side, so requests are checked against this set before they are built.
 */
const SUPPORTED_QUESTION_TYPES: ReadonlySet<AssessmentQuestionTypeEnum> = new Set(
  Object.values(AssessmentQuestionTypeEnum).filter(type => !CLIENT_ONLY_QUESTION_TYPES.includes(type))
);

@Injectable({
  providedIn: 'root'
})
export class AssessmentsMapperService {

  readonly CUSTOM_COMPONENT_TYPE = "custom-component" as const;

  private readonly loggerName = 'AssessmentsMapperService';

  constructor(
    private randomUUIDPipe: RandomUUIDPipe,
    private customFieldsV2Service: CustomFieldsV2Service,
    private camelCaseWithSpacesPipe: CamelCaseWithSpacesPipe,
    private customEntityChipBackgroundColorPipe: CustomEntityChipBackgroundColorPipe,
    private loggerService: LoggerService,
    private assessmentsLabelsService: AssessmentsLabelsService,
  ) {}

  /**
   * Resolves a template's labels to company label ids for the create-assessment request. OOB template
   * tags are label NAMES (`string[]` from Squidex), custom-template tags are label objects — handle
   * both, drop unresolved names. Ensures assessments inherit their template's use-case/regulation
   * labels so they appear under the Privacy/Vendor/AI tabs (product spec).
   */
  private resolveTemplateTagIds(tags?: (AssessmentCustomLabel | string)[]): number[] {
    if (!tags?.length) {
      return [];
    }
    return tags
      .map(tag => (typeof tag === 'string' ? this.assessmentsLabelsService.getLabelByName(tag)?.id : tag?.id))
      .map(id => Number(id))
      // Drop unresolved names / non-numeric ids — a string UUID would coerce to NaN.
      .filter(id => Number.isFinite(id));
  }

  private isSelectField(inputType: FieldInputType | CustomFieldInputTypeEnum | undefined): boolean {
    return inputType === FieldInputType.Select || inputType === CustomFieldInputTypeEnum.Select;
  }

  private isTextField(inputType: FieldInputType | CustomFieldInputTypeEnum | undefined): boolean {
    return inputType === FieldInputType.Text || inputType === CustomFieldInputTypeEnum.Text;
  }

  private getCustomFieldOptions(customFieldId: string): { id: string; name: string; color?: string; isDeleted?: boolean }[] | null {
    const field = this.customFieldsV2Service.getField(customFieldId);
    if (!field || !this.isSelectField(field.inputType)) return null;
    return (field.options ?? []).map(o => ({ id: o.id, name: o.text, color: o.color, isDeleted: o.isDeleted }));
  }

  // Page mapping
  mapAssessmentPage(assessmentRes: AssessmentPageResponse): AssessmentPage {
    return this.mapWithOriginalState(assessmentRes, {
      sections: assessmentRes.sections.map(section => this.mapAssessmentSection(assessmentRes.id, section)),
    });
  }

  // Section mapping
  mapAssessmentSection(
    assessmentPageId: AssessmentResourceId, 
    sectionRes: AssessmentSectionResponse
  ): AssessmentSection {
    return this.mapWithOriginalState(sectionRes, {
      formControlId: sectionRes.formControlId ?? this.randomUUIDPipe.transform(),
      assessmentPageId,
      isDeleted: false,
      isNew: false,
      questions: sectionRes.questions.map(question => this.mapAssessmentQuestion(assessmentPageId, sectionRes.id, question as AssessmentQuestion)),
    });
  }

  // Question mapping
  mapAssessmentQuestion(
    assessmentPageId: AssessmentResourceId,
    sectionId: AssessmentResourceId,
    questionRes: AssessmentQuestion
  ): AssessmentQuestion {
    return this.mapWithOriginalState(questionRes, {
      formControlId: questionRes.formControlId ?? this.randomUUIDPipe.transform(),
      assessmentPageId,
      isNew: false,
      isDeleted: false,
      sectionId,
      value: this.createQuestionValue(questionRes, questionRes.type as AssessmentQuestionTypeEnum),
    });
  }

  // Template mapping
  mapTemplateToAssessmentPageRequest( 
    template: AssessmentsMineOSTemplateResponse | AssessmentsTemplate, 
    name: string, 
    systemId?: string, 
    agentId?: string
  ): AssessmentPageRequest {
    const baseRequest = this.createBaseAssessmentRequest(name, agentId, systemId, template.type as AssessmentTemplateEnum);

    // Check if it's a MineOS template
    if (template.hasOwnProperty('schemaId') && template.hasOwnProperty('schemaName')) {
      const sections = this.transformMineOSTemplateToSections((template as AssessmentsMineOSTemplateResponse).template);
      const layout = this.getPageOrdinality(sections);
      // Inherit the OOB template's use-case/regulation labels onto the new assessment.
      const tagIds = this.resolveTemplateTagIds((template as AssessmentsMineOSTemplateResponse).tags);

      const response: AssessmentPageRequest = {
        ...baseRequest,
        sections,
        layout,
        ...(tagIds.length ? { tagIds } : {}),
      };

      if ((template as AssessmentsMineOSTemplateResponse)?.logic) {
        response.logic = (template as AssessmentsMineOSTemplateResponse)?.logic;
      }
      return response;
    }

    // Assessment template
    const assessmentTemplate = template as AssessmentsTemplate;
    const schema = this.deepJsonParse(assessmentTemplate.schema);
    const sections = schema.map(section => {
      let questions: AssessmentQuestionRequest[] = [];
      if (section.controls) {
        questions = section.controls.map(question => this.createQuestionRequest(question, true));
      }

      return {
        templateSectionId: section.id,
        formControlId: this.randomUUIDPipe.transform(),
        label: section.label,
        name: section.name ?? this.camelCaseWithSpacesPipe.transform(section.label),
        layout: this.getSectionOrdinality(questions),
        questions,
        // Section visibility (DevSectionVisibility): carry the template section's hidden-from-collaborators
        // flag onto the new assessment so a section hidden in the template stays hidden in the instance.
        isHiddenFromCollaborators: !!section.isHiddenFromCollaborators,
        // Section owner (DevAssessmentSectionOwners): snapshot the template section's owner onto the new
        // assessment at creation. Later template edits do not change existing assessments.
        ownerAgentId: section.ownerAgentId ?? null,
      };
    });

    const request = {
      ...baseRequest,
      logic: assessmentTemplate.logic,
      sections,
      tagIds: assessmentTemplate.tags?.map(t => t.id as number),
      templatePageId: assessmentTemplate.id?.toString(),
      layout: this.getPageOrdinality(sections),
    };
    
    return request;
  }

  // Helper to transform MineOS template to sections
  transformMineOSTemplateToSections(template: AssessmentsMineOSTemplate[]): AssessmentSectionRequest[] {
    const sections = [];
    template.forEach(section => {
      const questions = section.controls.map(question => this.createQuestionRequest(question, true));
      const layout = this.getSectionOrdinality(questions);

      sections.push({
        formControlId: this.randomUUIDPipe.transform(),
        templateSectionId: section.id,
        label: section.label,
        name: section.name ?? this.camelCaseWithSpacesPipe.transform(section.label),
        questions,
        layout,
        isHiddenFromCollaborators: !!section.isHiddenFromCollaborators,
      });
    });

    return sections;
  }

  // Helper to determine question type
  getQuestionType(
    params: {
      type: string;
      name: string;
      customFieldId?: string;
      options?: unknown[];
    }): AssessmentQuestionTypeEnum {
    // Check if params is null or undefined
    if (!params) {
      return null;
    }
    
    const { type, name, customFieldId } = params;

    if (customFieldId) {
      const inputType = this.customFieldsV2Service.getField(customFieldId)?.inputType;
      if (this.isSelectField(inputType)) {
        return AssessmentQuestionTypeEnum.DropdownCustomField;
      }
      else if (this.isTextField(inputType)) {
        return AssessmentQuestionTypeEnum.TextCustomField;
      }
      // The custom field is missing from the store, or its input type has no question-type
      // counterpart. Deliberately fall through to resolving by the raw `type` - a second route
      // into an unresolvable type, so make it visible rather than silent.
      this.loggerService.warn(
        this.loggerName,
        `Could not resolve question type from custom field, falling back to the raw type. customFieldId: "${customFieldId}", inputType: "${inputType}", type: "${type}"`
      );
    }
    else if ((type === this.CUSTOM_COMPONENT_TYPE && name) || (name?.includes("--"))) {
      const foundKey = Object.keys(AssessmentQuestionTypeEnum).find(key => 
        this.normalizeString(AssessmentQuestionTypeEnum[key]) === this.normalizeString(name.split("--")[0])
      );
      return AssessmentQuestionTypeEnum[foundKey] ?? this.getQuestionTypeByValue(type);
    }
    return this.getQuestionTypeByValue(type);
  }

  /**
   * Resolves a raw control `type` onto the enum, or `undefined` when it matches no member.
   * It never echoes back its unvalidated input: doing so let an unknown - or missing - type
   * flow into an assessment request, where it was dropped on serialization and surfaced as an
   * opaque server error instead of a diagnosable client-side one.
   */
  private getQuestionTypeByValue(val: string): AssessmentQuestionTypeEnum {
    return coreGetQuestionTypeByValue(val) as unknown as AssessmentQuestionTypeEnum;
  }

  // Helper to create layout object
  createLayoutObject(options: {
    disabled?: boolean;
    description?: string;
    multiple?: boolean;
    placeholder?: string;
    required?: boolean;
    tooltip?: string;
    validations?: unknown;
  }): string {
    return coreCreateLayoutObject(options);
  }

  // Helper to create base assessment request
  createBaseAssessmentRequest(
    name: string,
    agentId?: string,
    systemId?: string,
    type?: AssessmentTemplateEnum
  ): Omit<AssessmentPageRequest, 'sections'> {
    return coreCreateBaseAssessmentRequest(name, agentId, systemId, type) as unknown as Omit<AssessmentPageRequest, 'sections'>;
  }

  // Helper to create question value
  private createQuestionValue(question: AssessmentQuestion | AssessmentsMineOSFormControl, type: AssessmentQuestionTypeEnum): AssessmentResourceValue {
    const value = question.value;

    if (type == AssessmentQuestionTypeEnum.TextCustomField) {
      return {
        customFieldId: value?.['customFieldId'] ?? question?.['customFieldId'],
        textValue: value?.['textValue'] ?? question?.['textValue']
      } as AssessmentCustomFieldText;
    }
    else if (type == AssessmentQuestionTypeEnum.DropdownCustomField) {
      return {
        customFieldId: value?.['customFieldId'] ?? question?.['customFieldId'],
        options: this.createDropdownOptions(value?.['options'] ?? question?.['options'], value?.['customFieldId'] ?? question?.['customFieldId'])
      };
    } 
    else if (type === AssessmentQuestionTypeEnum.Dropdown) {
      return this.createDropdownOptions(value?.['options'] ?? question?.['options'] ?? value);
    }

    return value;
  }

  // Helper to create dropdown options
  private createDropdownOptions(options: AssessmentDropdownOption[] | DropdownOption[], customFieldId?: string): AssessmentDropdownOption[] {
    if (customFieldId) {
      const fieldOptions = this.getCustomFieldOptions(customFieldId);
      if (fieldOptions) {
        return fieldOptions.map(option => {
          const layout = option.color ? { chipColor: { textColor: 'var(--mine-dark-blue)', backgroundColor: this.customEntityChipBackgroundColorPipe.transform(option.color) } } : null;
          return {
            id: option.id,
            label: option.name,
            layout: layout ? JSON.stringify(layout) : undefined,
            isSelected: (options as AssessmentDropdownOption[]).find(o => o.id === option.id)?.isSelected ?? false,
            isDeleted: option.isDeleted,
          };
        });
      }
    }

    return options?.map(option => ({
      id: option.id,
      label: option.label ?? option.value,
      layout: this.normalizeOptionLayout(option?.layout),
      isSelected: (option as AssessmentDropdownOption).isSelected ?? false
    }));
  }

  /**
   * The API models an option's `Layout` as a string, so it must be serialized before it is sent -
   * emitting a parsed object here made deserialization throw inside the same
   * `PageCreateInput -> AssessmentPage` mapping the create request goes through.
   * Mirrors `normalizeLayoutValue` in `assessments-tracking-changes-helper.ts`, which is private
   * to that service: objects are stringified, strings are normalized through a parse/re-stringify
   * round-trip, and a string that isn't valid JSON is passed through untouched.
   * A falsy layout is returned as-is so an option with no layout stays absent rather than
   * becoming the string "null".
   */
  private normalizeOptionLayout(layout: unknown): string {
    return coreNormalizeOptionLayout(layout);
  }

  /**
   * Helper to create question request.
   * `strict` is opt-in and reserved for requests actually sent to the API: it rejects a question
   * whose type the backend cannot bind, naming the offending control. The template-builder
   * preview builds the same shape locally and must keep rendering an already-broken template,
   * so it stays non-strict.
   */
  createQuestionRequest(question: any, strict = false): AssessmentQuestionRequest {
    const type = this.getQuestionType(question);

    if (strict && !SUPPORTED_QUESTION_TYPES.has(type)) {
      const error = new UnsupportedQuestionTypeError(question?.name, question?.label, question?.templateQuestionId ?? question?.id, question?.type);
      this.loggerService.error(this.loggerName, error.message);
      throw error;
    }

    const layout = this.createLayoutObject({
      disabled: question.disabled,
      description: question.description,
      multiple: question.multiple,
      placeholder: question.placeholder,
      required: question.required,
      tooltip: question.tooltip,
      validations: question.validations,
    });

    const request = {
      templateQuestionId: question.id,
      formControlId: this.randomUUIDPipe.transform(),
      label: question.label,
      name: question.name ?? this.camelCaseWithSpacesPipe.transform(question.label),
      type,
      value: this.createQuestionValue(question, type),
      layout,
    } as AssessmentQuestionRequest;

    return request;
  }

  sortSectionsByOrdinality(instance: AssessmentPage): void {
    // Ensure layout exists so new sections are ordered by their current order
    if (!instance.layout) {
      instance.layout = this.getPageOrdinality(instance.sections as AssessmentSectionRequest[]);
    }
    instance.sections = this.sortByOrdinality(
      instance.sections,
      instance.layout,
      'sectionOrdinality'
    );
  }

  sortQuestionsByOrdinality(sections: AssessmentSection[]): void {
    sections.forEach(section => {
      // Ensure section layout exists so new sections without layout keep the current question order
      if (!section.layout) {
        section.layout = this.getSectionOrdinality(section.questions as AssessmentQuestionRequest[]);
      }
      section.questions = this.sortByOrdinality(
        section.questions,
        section.layout,
        'questionOrdinality'
      );
    });
  }

  private sortByOrdinality<T extends { formControlId?: AssessmentResourceId }>(
    items: T[],
    layout: any,
    ordinality: 'sectionOrdinality' | 'questionOrdinality'
  ): T[] {
    if (!layout) return items;

    const parsedLayout = typeof layout === 'string' ? JSON.parse(layout) : layout;
    const ordinalityArray = parsedLayout[ordinality];

    // Support both new array format and legacy object/map/tuple format for backward compatibility
    if (ordinalityArray) {
      let orderArray: string[] = [];
      
      if (Array.isArray(ordinalityArray)) {
        const isTupleArray = ordinalityArray.every((item: unknown) => Array.isArray(item) && item.length >= 2);
        if (isTupleArray) {
          // Legacy tuple array format: [[formControlId, index], ...]
          const sortedTuples = [...ordinalityArray].sort((a: any[], b: any[]) => Number(a[1]) - Number(b[1]));
          orderArray = sortedTuples.map(tuple => String(tuple[0]));
        } else {
          // New format: simple array of formControlIds - index represents position
          orderArray = ordinalityArray.map(id => String(id));
        }
      } else if (typeof ordinalityArray === 'object') {
        // Legacy format: object or map - convert to array by sorting by values
        const entries = Object.entries(ordinalityArray);
        entries.sort((a, b) => Number(a[1]) - Number(b[1]));
        orderArray = entries.map(([key]) => String(key));
      }

      if (orderArray.length > 0) {
        // Create a map for quick lookup of position in ordinality array
        const positionMap = new Map<string, number>();
        orderArray.forEach((id, index) => {
          positionMap.set(String(id), index);
        });

        // Create a map of original indices to preserve order for items without ordinality
        const originalIndexMap = new Map<T, number>();
        items.forEach((item, index) => {
          originalIndexMap.set(item, index);
        });

        // Use a large number to ensure items without ordinality are placed at the end
        const MAX_ORDINALITY_INDEX = 1000000;

        return [...items].sort((a, b) => {
          const aKey = a.formControlId?.toString() ?? '';
          const bKey = b.formControlId?.toString() ?? '';
          
          const aIndex = positionMap.get(aKey) ?? MAX_ORDINALITY_INDEX;
          const bIndex = positionMap.get(bKey) ?? MAX_ORDINALITY_INDEX;
          
          // If both have ordinality, sort by ordinality
          if (aIndex !== MAX_ORDINALITY_INDEX && bIndex !== MAX_ORDINALITY_INDEX) {
            return aIndex - bIndex;
          }
          
          // If only one has ordinality, it comes first
          if (aIndex !== MAX_ORDINALITY_INDEX) {
            return -1;
          }
          if (bIndex !== MAX_ORDINALITY_INDEX) {
            return 1;
          }
          
          // If neither has ordinality, preserve original order
          const aOriginalIndex = originalIndexMap.get(a) ?? 0;
          const bOriginalIndex = originalIndexMap.get(b) ?? 0;
          return aOriginalIndex - bOriginalIndex;
        });
      }
    }

    return items;
  }

  /**
   * Normalizes a layout object or string and converts ordinality arrays/objects
   * into ES Maps for easier processing (for backward compatibility).
   * Supports both new array format and legacy object format.
   * @param layout - The layout object or JSON string.
   * @returns Normalized layout with Maps for ordinality fields, or null if invalid.
   */
  normalizeLayoutObject(layout: unknown): { sectionOrdinality?: Map<string, number>; questionOrdinality?: Map<string, number> } | null {
    if (!layout) return null;

    let parsed;
    if (typeof layout === 'string') {
        try {
            parsed = JSON.parse(layout);
        } catch (err) {
            console.error('Failed to parse layout string:', err);
            return null;
        }
    } else if (typeof layout === 'object') {
        parsed = layout;
    } else {
        return null;
    }

    const result: { sectionOrdinality?: Map<string, number>; questionOrdinality?: Map<string, number> } = {};

    // Handle sectionOrdinality - support array (new), tuple array (legacy), and object (legacy) formats
    if (parsed.sectionOrdinality) {
      if (Array.isArray(parsed.sectionOrdinality)) {
        const isTupleArray = parsed.sectionOrdinality.every((item: unknown) => Array.isArray(item) && item.length >= 2);
        if (isTupleArray) {
          // Legacy tuple array: [[id, index], ...]
          const sortedTuples = [...parsed.sectionOrdinality].sort((a: any[], b: any[]) => Number(a[1]) - Number(b[1]));
          result.sectionOrdinality = new Map<string, number>(sortedTuples.map((tuple: any[], index: number) => [String(tuple[0]), index]));
        } else {
          // New format: array of formControlIds - convert to Map where index is the value
          const map = new Map<string, number>();
          parsed.sectionOrdinality.forEach((id: string, index: number) => {
            map.set(String(id), index);
          });
          result.sectionOrdinality = map;
        }
      } else if (typeof parsed.sectionOrdinality === 'object') {
        // Legacy format: object with formControlId -> index mapping
        result.sectionOrdinality = new Map<string, number>(Object.entries(parsed.sectionOrdinality).map(([key, value]) => [String(key), Number(value)]));
      }
    }

    // Handle questionOrdinality - support array (new), tuple array (legacy), and object (legacy) formats
    if (parsed.questionOrdinality) {
      if (Array.isArray(parsed.questionOrdinality)) {
        const isTupleArray = parsed.questionOrdinality.every((item: unknown) => Array.isArray(item) && item.length >= 2);
        if (isTupleArray) {
          // Legacy tuple array: [[id, index], ...]
          const sortedTuples = [...parsed.questionOrdinality].sort((a: any[], b: any[]) => Number(a[1]) - Number(b[1]));
          result.questionOrdinality = new Map<string, number>(sortedTuples.map((tuple: any[], index: number) => [String(tuple[0]), index]));
        } else {
          // New format: array of formControlIds - convert to Map where index is the value
          const map = new Map<string, number>();
          parsed.questionOrdinality.forEach((id: string, index: number) => {
            map.set(String(id), index);
          });
          result.questionOrdinality = map;
        }
      } else if (typeof parsed.questionOrdinality === 'object') {
        // Legacy format: object with formControlId -> index mapping
        result.questionOrdinality = new Map<string, number>(Object.entries(parsed.questionOrdinality).map(([key, value]) => [String(key), Number(value)]));
      }
    }

    return Object.keys(result).length > 0 ? result : null;
  }

  private getPageOrdinality(sections: AssessmentSectionRequest[]): string {
    return coreGetPageOrdinality(sections as unknown as CoreSectionRequest[]);
  }

  private getSectionOrdinality(questions: AssessmentQuestionRequest[]): string {
    return coreGetSectionOrdinality(questions as unknown as CoreQuestionRequest[]);
  }

  // Helper to map with original state
  private mapWithOriginalState<T, R extends Record<string, any>>(
    source: T, 
    additionalProps: R
  ): T & R & { originalState: T } {
    return {
      ...source,
      ...additionalProps,
      originalState: structuredClone(source)
    };
  }

  // Helper to deep parse JSON
  private deepJsonParse(input: string): any {
    return coreDeepJsonParse(input);
  }

  getQuestionLayout(control: AssessmentQuestion): any {
    if (!!control.layout) {
      if (typeof control.layout === 'string') {
        return JSON.parse(control.layout as string);
      }
      return control.layout;
    }
    return null;
  }

  private normalizeString(str: string): string {
    return coreNormalizeString(str);
  }
}